import equal from "deep-equal";

import { isDefined } from "~/shared/common";
import type { Comparator } from "~/shared/types";

export const removeFromSorted = (sortedDuids: string[], duid: string) => {
  const index = sortedDuids.indexOf(duid);
  if (index === -1) {
    return;
  }
  sortedDuids.splice(index, 1);
};

export const updateInSorted = <T extends { duid: string }>(
  duidsToObjects: Map<string, T>,
  sortedDuids: string[],
  newDuid: string,
  comparator: Comparator<T>
) => {
  removeFromSorted(sortedDuids, newDuid);

  const newEntry = duidsToObjects.get(newDuid);
  if (!newEntry) {
    return;
  }

  const insertIndex = sortedDuids.findIndex((duid) => {
    const existingEntry = duidsToObjects.get(duid);
    if (!existingEntry) {
      return false;
    }
    return comparator(newEntry, existingEntry) < 0;
  });

  if (insertIndex === -1) {
    sortedDuids.push(newDuid);
  } else {
    sortedDuids.splice(insertIndex, 0, newDuid);
  }
};

export const setObjectMapAndSorted = <T extends { duid: string }>(
  duidsToObjects: Map<string, T>,
  sortedDuids: string[],
  newObjects: T[],
  comparator: Comparator<T>
) => {
  const newIds = new Set(newObjects.map((e) => e.duid));
  Array.from(duidsToObjects.keys()).forEach((id) => {
    if (newIds.has(id)) {
      return;
    }
    duidsToObjects.delete(id);
  });
  newObjects.forEach((obj) => {
    const existingObj = duidsToObjects.get(obj.duid);
    if (equal(existingObj, obj, { strict: true })) {
      return;
    }
    duidsToObjects.set(obj.duid, obj);
  });

  const newSortedKeys = [...newObjects].sort(comparator).map((e) => e.duid);
  sortedDuids.splice(0, sortedDuids.length, ...newSortedKeys);
};

const getOrCreateArray = (map: Map<string, string[]>, key: string): string[] => {
  let array = map.get(key);
  if (!array) {
    array = [];
    map.set(key, array);
  }
  return array;
};

export const removeFromSortedMap = (sortedDuidsMap: Map<string, string[]>, mapKey: string, duid: string) => {
  const sortedDuids = getOrCreateArray(sortedDuidsMap, mapKey);
  removeFromSorted(sortedDuids, duid);
};

export const updateInSortedMap = <T extends { duid: string }>(
  duidsToObjects: Map<string, T>,
  sortedDuidsMap: Map<string, string[]>,
  mapKey: string,
  newDuid: string,
  makeComparator: (objects: T[]) => Comparator<T>
) => {
  const sortedDuids = getOrCreateArray(sortedDuidsMap, mapKey);
  const existingObjects = sortedDuids.map((duid) => duidsToObjects.get(duid)).filter(isDefined);
  const newObject = duidsToObjects.get(newDuid);
  const allObjects = newObject && !sortedDuids.includes(newDuid) ? [...existingObjects, newObject] : existingObjects;
  const comparator = makeComparator(allObjects);
  updateInSorted(duidsToObjects, sortedDuids, newDuid, comparator);
};

export const setObjectMapAndSortedMapGrouped = <T extends { duid: string; propertyDuid: string }>(
  duidsToObjects: Map<string, T>,
  sortedDuidsMap: Map<string, string[]>,
  newObjects: T[],
  makeComparator: (objects: T[]) => Comparator<T>
) => {
  const objectsByPropertyDuid = new Map<string, T[]>();
  newObjects.forEach((obj) => {
    const propertyObjects = objectsByPropertyDuid.get(obj.propertyDuid) ?? [];
    if (propertyObjects.length === 0) {
      objectsByPropertyDuid.set(obj.propertyDuid, propertyObjects);
    }
    propertyObjects.push(obj);
  });

  const newIds = new Set(newObjects.map((e) => e.duid));
  Array.from(duidsToObjects.keys()).forEach((id) => {
    if (!newIds.has(id)) {
      duidsToObjects.delete(id);
    }
  });

  newObjects.forEach((obj) => {
    const existingObj = duidsToObjects.get(obj.duid);
    if (!equal(existingObj, obj, { strict: true })) {
      duidsToObjects.set(obj.duid, obj);
    }
  });

  objectsByPropertyDuid.forEach((propertyObjects, propertyDuid) => {
    const sortedDuids = getOrCreateArray(sortedDuidsMap, propertyDuid);
    const comparator = makeComparator(propertyObjects);
    const newSortedKeys = [...propertyObjects].sort(comparator).map((e) => e.duid);
    sortedDuids.splice(0, sortedDuids.length, ...newSortedKeys);
  });
};
